import React, {useEffect, useRef, useState} from "react";
import {yupResolver} from "@hookform/resolvers/yup";
import {css, cx} from "@panda/css";
import lscache from "lscache";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {FormProviderRHF, InputField} from "@pg-design/rhf-components-module";
import {Highlight, Text} from "@pg-design/text-module";
import {isFetchBaseQueryError, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import {phoneNumberFormat} from "@pg-mono/string-utils";

import {closeFlowVariantB, nextStepVariantB, RegistrationFlowStepVariantB, setStepVariantB} from "../../auth/state/registration_flow_variantB_slice";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {HOTLINE_INFO_PHONE_NUMBER} from "../../hotline/constants/hotline_phone_numbers";
import {addNotification} from "../../notifications/state/notifications_slice";
import {defaultAPIErrorNotification} from "../../notifications/utils/error_notifications";
import {useAppDispatch} from "../../store/hooks";
import {gtmEventPhoneVerification} from "../../tracking/google_tag_manager/gtm_event_phone_verification";
import {getTimeInMinutes} from "../../utils/get_time_in_minutes";
import {useGetUserProfileQuery} from "../api/get_user_profile";
import {usePostUserPhoneVerificationCodeMutation} from "../api/post_user_phone_verification_code";
import {usePostUserPhoneVerifyMutation} from "../api/post_user_phone_verify";
import {useApiErrorHandler} from "../hooks/use_api_error_handler";
import {digitFields, IFormData, useDigitInputNavigation} from "../hooks/use_digit_input_navigation";
import {useWebOtp} from "../hooks/use_web_otp";
import {getPhoneVerificationCodeRemainingTime} from "../utils/get_phone_verification_code_remaining_time";

interface IProps {
    isOpen: boolean;
    onSuccess?: () => void;
}

export const CODE_RESEND_DELAY_IN_SECONDS = 60;
const HTTP_STATUS_FOR_TOO_MANY_REQUESTS = 429;
const PHONE_VERIFICATION_TIMER_START_LS_KEY = "phoneVerificationTimerStart";

const digitValidation = Yup.number()
    .transform((value, originalValue) => {
        // Transform empty string or non-numeric strings to null
        if (originalValue === "" || isNaN(originalValue)) {
            return null;
        }
        return Number(originalValue);
    })
    .nullable()
    .required("Niepoprawna wartość")
    .min(0, "Niepoprawna wartość")
    .max(9, "Niepoprawna wartość");

const validationSchema = Yup.object().shape({
    firstDigit: digitValidation,
    secondDigit: digitValidation,
    thirdDigit: digitValidation,
    fourthDigit: digitValidation
});

export const UserPhoneVerification = (props: IProps) => {
    const {isOpen, onSuccess} = props;
    const dispatch = useAppDispatch();
    const {data: userInfo} = useGetUserProfileQuery();
    const [formError, setFormError] = useState<{message: string; status: number} | null>(null);
    const [sendPhoneVerificationCode, {isLoading}] = usePostUserPhoneVerificationCodeMutation();
    const [postUserPhoneVerify] = usePostUserPhoneVerifyMutation();
    const {hasCompleteProfile, hasPhoneVerified} = useShouldHidePrices();

    const inputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null]);
    const digitNavigation = useDigitInputNavigation(inputRefs);
    const prevIsOpenRef = useRef<boolean>(false);

    const [timerStartTime, setTimerStartTime] = useState<number | null>(() => {
        const savedStartTime = lscache.get(PHONE_VERIFICATION_TIMER_START_LS_KEY);
        return savedStartTime ? parseInt(savedStartTime, 10) : null;
    });

    const [timer, setTimer] = useState(() => getPhoneVerificationCodeRemainingTime(timerStartTime));
    const formattedTime = getTimeInMinutes(timer);
    const hotlinePhoneFormat = phoneNumberFormat(HOTLINE_INFO_PHONE_NUMBER);

    useEffect(() => {
        if (timerStartTime) {
            const interval = setInterval(() => {
                const remaining = getPhoneVerificationCodeRemainingTime(timerStartTime);
                setTimer(remaining);

                if (remaining === 0) {
                    lscache.remove(PHONE_VERIFICATION_TIMER_START_LS_KEY);
                    clearInterval(interval);
                }
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [timerStartTime]);

    useEffect(() => {
        if (userInfo && userInfo.phone && userInfo.phone_verified) {
            dispatch(nextStepVariantB({hasCompleteProfile, hasPhoneVerified}));
        }
    }, [userInfo, dispatch]);

    useEffect(() => {
        if (isOpen && formError?.status !== HTTP_STATUS_FOR_TOO_MANY_REQUESTS) {
            const currentRemainingTime = getPhoneVerificationCodeRemainingTime(timerStartTime);

            // Only send GTM event when modal transitions from closed to open
            if (!prevIsOpenRef.current) {
                gtmEventPhoneVerification("view");
            }
            prevIsOpenRef.current = isOpen;

            if (currentRemainingTime === 0 || !timerStartTime) {
                handlers.setNewStartTime();
                sendNewCode();
            } else {
                setTimer(currentRemainingTime);
            }
        } else {
            prevIsOpenRef.current = isOpen;
        }
    }, [isOpen, sendPhoneVerificationCode, formError]);

    const form = useFormRHF<IFormData>({
        resolver: yupResolver(validationSchema),
        mode: "onBlur",
        defaultValues: {
            firstDigit: null,
            secondDigit: null,
            thirdDigit: null,
            fourthDigit: null
        }
    });

    const handleWebOtpCode = (code: string) => {
        const digits = code.replace(/[^0-9]/g, "").slice(0, 4);

        digits.split("").forEach((digit, index) => {
            if (index < digitFields.length) {
                form.setValue(digitFields[index], parseInt(digit), {shouldValidate: true});
            }
        });

        if (digits.length > 0) {
            const lastIndex = Math.min(digits.length, 4) - 1;
            digitNavigation.focusFieldByIndex(lastIndex);
        }
    };

    useWebOtp({
        enabled: isOpen,
        onCodeReceived: handleWebOtpCode
    });

    const handlers = {
        onCloseButton: () => {
            dispatch(closeFlowVariantB());
        },
        changeNumberButton: () => {
            dispatch(setStepVariantB({step: RegistrationFlowStepVariantB.PHONE_COMPLETION}));
        },
        clearErrorMessage: () => {
            setFormError((prevState) => (prevState ? {...prevState, message: ""} : null));
        },
        setNewStartTime: () => {
            const startTime = Date.now();
            setTimerStartTime(startTime);
            lscache.set(PHONE_VERIFICATION_TIMER_START_LS_KEY, startTime.toString());
        },
        clearStartTime: () => {
            setTimerStartTime(null);
            lscache.remove(PHONE_VERIFICATION_TIMER_START_LS_KEY);
        },
        inputFocusChange: (fieldName: keyof IFormData, value: string) => {
            const numericValue = value.replace(/[^0-9]/g, "");
            form.setValue(fieldName, numericValue ? parseInt(numericValue.slice(-1)) : null);

            // Move to next input if value is entered
            if (numericValue && numericValue.length > 0) {
                digitNavigation.moveToNextField(fieldName);
            }
        },
        inputKeyDown: (fieldName: keyof IFormData, event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Backspace") {
                const currentValue = (event.target as HTMLInputElement).value;

                // If current field is empty, move to previous field
                if (!currentValue) {
                    digitNavigation.moveToPreviousField(fieldName);
                }
            }
        },
        inputPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
            event.preventDefault();
            const pastedText = event.clipboardData.getData("text");
            const digits = pastedText.replace(/[^0-9]/g, "").slice(0, 4);

            if (digits.length === 0) {
                return;
            }

            digits.split("").forEach((digit, index) => {
                if (index < digitFields.length) {
                    form.setValue(digitFields[index], parseInt(digit), {shouldValidate: true});
                }
            });

            const nextEmptyIndex = digits.length < 4 ? digits.length : 3;
            digitNavigation.focusFieldByIndex(nextEmptyIndex);
        }
    };

    const sendNewCode = async () => {
        const currentRemainingTime = getPhoneVerificationCodeRemainingTime(timerStartTime);

        if (currentRemainingTime > 0) {
            return null;
        }

        handlers.setNewStartTime();
        handlers.clearErrorMessage();
        const response = await sendPhoneVerificationCode();

        if (isFetchBaseQueryError(response.error)) {
            const error = response.error;

            if (error.status === HTTP_STATUS_FOR_TOO_MANY_REQUESTS) {
                // Clear timer and localStorage for 429 error status
                handlers.clearStartTime();
                setTimer(0);
            }

            const errorResult = useApiErrorHandler(error);
            if (errorResult) {
                setFormError(errorResult);
            } else {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
            }
        }
    };

    const onSubmit = async (values: IFormData) => {
        const phoneVerificationCode = `${values.firstDigit}${values.secondDigit}${values.thirdDigit}${values.fourthDigit}`;

        handlers.clearErrorMessage();
        const response = await postUserPhoneVerify({verification_code: phoneVerificationCode});

        if (isFetchBaseQueryError(response.error)) {
            const errorResult = useApiErrorHandler(response.error);
            if (errorResult) {
                setFormError(errorResult);
            } else {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
            }
        } else {
            gtmEventPhoneVerification("submitted");
            onSuccess?.();
            lscache.remove(PHONE_VERIFICATION_TIMER_START_LS_KEY);
        }
    };

    useEffect(() => {
        const subscription = form.watch((values) => {
            const {firstDigit, secondDigit, thirdDigit, fourthDigit} = values;

            // Check if all digits are filled and valid
            if (firstDigit !== null && secondDigit !== null && thirdDigit !== null && fourthDigit !== null) {
                // Trigger form submission automatically
                form.handleSubmit(onSubmit)();
            }
        });

        return () => subscription.unsubscribe();
    }, [form, onSubmit, formError]);

    if (!userInfo || !userInfo.phone) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onModalClose={handlers.onCloseButton} className={modalStyle}>
            <Modal.CloseButton placement="right" variant="rounded" className={css({m: 2})} />
            <Modal.Content>
                <Text variant="headline_3" align="left" mb={1} className={css({pr: 1})}>
                    Zweryfikuj swój <Highlight>numer telefonu</Highlight>
                </Text>
                <Text variant="body_copy_2" mb={4} color="var(--colors-gray-800)">
                    Wpisz 4 cyfrowy kod aktywacyjny, który wysłaliśmy na Twój numer telefonu{" "}
                    <span className={boldTextStyle}>{phoneNumberFormat(userInfo.phone)}</span>.{" "}
                    <span className={changeNumberButtonStyle} onClick={handlers.changeNumberButton}>
                        Zmień numer
                    </span>
                </Text>
                <FormProviderRHF {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <div className={formStyle}>
                            {digitFields.map((fieldName, index) => (
                                <InputField
                                    key={fieldName}
                                    ref={(el) => {
                                        inputRefs.current[index] = el;
                                    }}
                                    name={fieldName}
                                    maxLength={1}
                                    className={inputStyle}
                                    inputMode="numeric"
                                    autoComplete={index === 0 ? "one-time-code" : "off"}
                                    onChange={(e) => handlers.inputFocusChange(fieldName, e.target.value)}
                                    onKeyDown={(e) => handlers.inputKeyDown(fieldName, e)}
                                    onPaste={handlers.inputPaste}
                                />
                            ))}
                        </div>
                        {formError && (
                            <Text variant="info_txt_1" className={errorMessageStyle}>
                                {formError.message}
                            </Text>
                        )}
                        <Button variant="filled_primary" type="submit" fullWidth className={buttonStyle}>
                            POTWIERDŹ
                        </Button>
                        {formError?.status !== HTTP_STATUS_FOR_TOO_MANY_REQUESTS ? (
                            <Text variant="info_txt_1" as="div" className={timer > 0 ? timerStyle : sendCodeInfoStyle} onClick={sendNewCode}>
                                {isLoading ? <Loader size="sm" /> : timer > 0 ? `Wyślij kod ponownie za: ${formattedTime}` : "Wyślij kod ponownie"}
                            </Text>
                        ) : (
                            <Text variant="info_txt_1" className={timerStyle}>
                                Nie otrzymałeś kodu? <br />
                                Skontaktuj się z naszym biurem obsługi:{" "}
                                <a className={cx(timerStyle, css({fontWeight: 700}))} href={`tel:+${hotlinePhoneFormat}`}>
                                    {phoneNumberFormat(HOTLINE_INFO_PHONE_NUMBER)}
                                </a>
                            </Text>
                        )}
                    </form>
                </FormProviderRHF>
            </Modal.Content>
        </Modal>
    );
};

//Styles
const modalStyle = css({
    sm: {
        p: 2,
        borderRadius: 2,
        width: "544px",
        height: "448px"
    }
});

const formStyle = css({
    display: "flex",
    flexDirection: "row",
    gap: 1,
    mb: 4,
    height: "72px",
    width: "72px",
    justifyContent: "center",
    margin: "0 auto",

    sm: {
        mb: 3,
        height: "112px",
        width: "112px"
    }
});

const buttonStyle = css({
    alignItems: "center",
    mb: 2
});

const boldTextStyle = css({
    fontWeight: "700"
});

const changeNumberButtonStyle = css({
    textDecoration: "underline",
    cursor: "pointer"
});

const inputStyle = css({
    textAlign: "center",
    borderRadius: "8px",
    border: "1px solid var(--colors-gray-600)",
    fontSize: "30.79px",
    sm: {
        fontSize: "35.92px"
    },
    "& div": {
        border: "none"
    },
    "& > div:first-child": {
        height: "72px",
        width: "72px",
        sm: {
            height: "112px",
            width: "112px"
        },

        "& > div:first-child": {
            height: "70px",
            width: "72px",
            sm: {
                height: "110px",
                width: "112px"
            }
        }
    },
    "& input": {
        height: "100%",
        width: "100%",
        textAlign: "center",
        fontWeight: "600"
    },
    '& [data-name="field-message"]': {
        display: "flex",
        justifyContent: "center"
    }
});

const timerStyle = css({
    color: "var(--colors-gray-800)",
    textAlign: "center"
});

const sendCodeInfoStyle = css({
    color: "var(--colors-gray-800)",
    textAlign: "center",
    cursor: "pointer",
    textDecoration: "underline"
});

const errorMessageStyle = css({
    color: "var(--colors-danger)",
    textAlign: "center",
    mb: 2
});
import React, {useEffect, useRef, useState} from "react";
import {yupResolver} from "@hookform/resolvers/yup";
import {css, cx} from "@panda/css";
import lscache from "lscache";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {Loader} from "@pg-design/loader-module";
import {Modal} from "@pg-design/modal-module";
import {FormProviderRHF, InputField} from "@pg-design/rhf-components-module";
import {Highlight, Text} from "@pg-design/text-module";
import {isFetchBaseQueryError, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import {phoneNumberFormat} from "@pg-mono/string-utils";

import {closeFlowVariantB, nextStepVariantB, RegistrationFlowStepVariantB, setStepVariantB} from "../../auth/state/registration_flow_variantB_slice";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {HOTLINE_INFO_PHONE_NUMBER} from "../../hotline/constants/hotline_phone_numbers";
import {addNotification} from "../../notifications/state/notifications_slice";
import {defaultAPIErrorNotification} from "../../notifications/utils/error_notifications";
import {useAppDispatch} from "../../store/hooks";
import {gtmEventPhoneVerification} from "../../tracking/google_tag_manager/gtm_event_phone_verification";
import {getTimeInMinutes} from "../../utils/get_time_in_minutes";
import {useGetUserProfileQuery} from "../api/get_user_profile";
import {usePostUserPhoneVerificationCodeMutation} from "../api/post_user_phone_verification_code";
import {usePostUserPhoneVerifyMutation} from "../api/post_user_phone_verify";
import {useApiErrorHandler} from "../hooks/use_api_error_handler";
import {digitFields, IFormData, useDigitInputNavigation} from "../hooks/use_digit_input_navigation";
import {useWebOtp} from "../hooks/use_web_otp";
import {getPhoneVerificationCodeRemainingTime} from "../utils/get_phone_verification_code_remaining_time";

interface IProps {
    isOpen: boolean;
    onSuccess?: () => void;
}

export const CODE_RESEND_DELAY_IN_SECONDS = 60;
const HTTP_STATUS_FOR_TOO_MANY_REQUESTS = 429;
const PHONE_VERIFICATION_TIMER_START_LS_KEY = "phoneVerificationTimerStart";

const digitValidation = Yup.number()
    .transform((value, originalValue) => {
        // Transform empty string or non-numeric strings to null
        if (originalValue === "" || isNaN(originalValue)) {
            return null;
        }
        return Number(originalValue);
    })
    .nullable()
    .required("Niepoprawna wartość")
    .min(0, "Niepoprawna wartość")
    .max(9, "Niepoprawna wartość");

const validationSchema = Yup.object().shape({
    firstDigit: digitValidation,
    secondDigit: digitValidation,
    thirdDigit: digitValidation,
    fourthDigit: digitValidation
});

export const UserPhoneVerification = (props: IProps) => {
    const {isOpen, onSuccess} = props;
    const dispatch = useAppDispatch();
    const {data: userInfo} = useGetUserProfileQuery();
    const [formError, setFormError] = useState<{message: string; status: number} | null>(null);
    const [sendPhoneVerificationCode, {isLoading}] = usePostUserPhoneVerificationCodeMutation();
    const [postUserPhoneVerify] = usePostUserPhoneVerifyMutation();
    const {hasCompleteProfile, hasPhoneVerified} = useShouldHidePrices();

    const inputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null]);
    const digitNavigation = useDigitInputNavigation(inputRefs);
    const prevIsOpenRef = useRef<boolean>(false);

    const [timerStartTime, setTimerStartTime] = useState<number | null>(() => {
        const savedStartTime = lscache.get(PHONE_VERIFICATION_TIMER_START_LS_KEY);
        return savedStartTime ? parseInt(savedStartTime, 10) : null;
    });

    const [timer, setTimer] = useState(() => getPhoneVerificationCodeRemainingTime(timerStartTime));
    const formattedTime = getTimeInMinutes(timer);
    const hotlinePhoneFormat = phoneNumberFormat(HOTLINE_INFO_PHONE_NUMBER);

    useEffect(() => {
        if (timerStartTime) {
            const interval = setInterval(() => {
                const remaining = getPhoneVerificationCodeRemainingTime(timerStartTime);
                setTimer(remaining);

                if (remaining === 0) {
                    lscache.remove(PHONE_VERIFICATION_TIMER_START_LS_KEY);
                    clearInterval(interval);
                }
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [timerStartTime]);

    useEffect(() => {
        if (userInfo && userInfo.phone && userInfo.phone_verified) {
            dispatch(nextStepVariantB({hasCompleteProfile, hasPhoneVerified}));
        }
    }, [userInfo, dispatch]);

    useEffect(() => {
        if (isOpen && formError?.status !== HTTP_STATUS_FOR_TOO_MANY_REQUESTS) {
            const currentRemainingTime = getPhoneVerificationCodeRemainingTime(timerStartTime);

            // Only send GTM event when modal transitions from closed to open
            if (!prevIsOpenRef.current) {
                gtmEventPhoneVerification("view");
            }
            prevIsOpenRef.current = isOpen;

            if (currentRemainingTime === 0 || !timerStartTime) {
                handlers.setNewStartTime();
                sendNewCode();
            } else {
                setTimer(currentRemainingTime);
            }
        } else {
            prevIsOpenRef.current = isOpen;
        }
    }, [isOpen, sendPhoneVerificationCode, formError]);

    const form = useFormRHF<IFormData>({
        resolver: yupResolver(validationSchema),
        mode: "onBlur",
        defaultValues: {
            firstDigit: null,
            secondDigit: null,
            thirdDigit: null,
            fourthDigit: null
        }
    });

    const handleWebOtpCode = (code: string) => {
        const digits = code.replace(/[^0-9]/g, "").slice(0, 4);

        digits.split("").forEach((digit, index) => {
            if (index < digitFields.length) {
                form.setValue(digitFields[index], parseInt(digit), {shouldValidate: true});
            }
        });

        if (digits.length > 0) {
            const lastIndex = Math.min(digits.length, 4) - 1;
            digitNavigation.focusFieldByIndex(lastIndex);
        }
    };

    useWebOtp({
        enabled: isOpen,
        onCodeReceived: handleWebOtpCode
    });

    const handlers = {
        onCloseButton: () => {
            dispatch(closeFlowVariantB());
        },
        changeNumberButton: () => {
            dispatch(setStepVariantB({step: RegistrationFlowStepVariantB.PHONE_COMPLETION}));
        },
        clearErrorMessage: () => {
            setFormError((prevState) => (prevState ? {...prevState, message: ""} : null));
        },
        setNewStartTime: () => {
            const startTime = Date.now();
            setTimerStartTime(startTime);
            lscache.set(PHONE_VERIFICATION_TIMER_START_LS_KEY, startTime.toString());
        },
        clearStartTime: () => {
            setTimerStartTime(null);
            lscache.remove(PHONE_VERIFICATION_TIMER_START_LS_KEY);
        },
        inputFocusChange: (fieldName: keyof IFormData, value: string) => {
            const numericValue = value.replace(/[^0-9]/g, "");
            form.setValue(fieldName, numericValue ? parseInt(numericValue.slice(-1)) : null);

            // Move to next input if value is entered
            if (numericValue && numericValue.length > 0) {
                digitNavigation.moveToNextField(fieldName);
            }
        },
        inputKeyDown: (fieldName: keyof IFormData, event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Backspace") {
                const currentValue = (event.target as HTMLInputElement).value;

                // If current field is empty, move to previous field
                if (!currentValue) {
                    digitNavigation.moveToPreviousField(fieldName);
                }
            }
        },
        inputPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
            event.preventDefault();
            const pastedText = event.clipboardData.getData("text");
            const digits = pastedText.replace(/[^0-9]/g, "").slice(0, 4);

            if (digits.length === 0) {
                return;
            }

            digits.split("").forEach((digit, index) => {
                if (index < digitFields.length) {
                    form.setValue(digitFields[index], parseInt(digit), {shouldValidate: true});
                }
            });

            const nextEmptyIndex = digits.length < 4 ? digits.length : 3;
            digitNavigation.focusFieldByIndex(nextEmptyIndex);
        }
    };

    const sendNewCode = async () => {
        const currentRemainingTime = getPhoneVerificationCodeRemainingTime(timerStartTime);

        if (currentRemainingTime > 0) {
            return null;
        }

        handlers.setNewStartTime();
        handlers.clearErrorMessage();
        const response = await sendPhoneVerificationCode();

        if (isFetchBaseQueryError(response.error)) {
            const error = response.error;

            if (error.status === HTTP_STATUS_FOR_TOO_MANY_REQUESTS) {
                // Clear timer and localStorage for 429 error status
                handlers.clearStartTime();
                setTimer(0);
            }

            const errorResult = useApiErrorHandler(error);
            if (errorResult) {
                setFormError(errorResult);
            } else {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
            }
        }
    };

    const onSubmit = async (values: IFormData) => {
        const phoneVerificationCode = `${values.firstDigit}${values.secondDigit}${values.thirdDigit}${values.fourthDigit}`;

        handlers.clearErrorMessage();
        const response = await postUserPhoneVerify({verification_code: phoneVerificationCode});

        if (isFetchBaseQueryError(response.error)) {
            const errorResult = useApiErrorHandler(response.error);
            if (errorResult) {
                setFormError(errorResult);
            } else {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
            }
        } else {
            gtmEventPhoneVerification("submitted");
            onSuccess?.();
            lscache.remove(PHONE_VERIFICATION_TIMER_START_LS_KEY);
        }
    };

    useEffect(() => {
        const subscription = form.watch((values) => {
            const {firstDigit, secondDigit, thirdDigit, fourthDigit} = values;

            // Check if all digits are filled and valid
            if (firstDigit !== null && secondDigit !== null && thirdDigit !== null && fourthDigit !== null) {
                // Trigger form submission automatically
                form.handleSubmit(onSubmit)();
            }
        });

        return () => subscription.unsubscribe();
    }, [form, onSubmit, formError]);

    if (!userInfo || !userInfo.phone) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onModalClose={handlers.onCloseButton} className={modalStyle}>
            <Modal.CloseButton placement="right" variant="rounded" className={css({m: 2})} />
            <Modal.Content>
                <Text variant="headline_3" align="left" mb={1} className={css({pr: 1})}>
                    Zweryfikuj swój <Highlight>numer telefonu</Highlight>
                </Text>
                <Text variant="body_copy_2" mb={4} color="var(--colors-gray-800)">
                    Wpisz 4 cyfrowy kod aktywacyjny, który wysłaliśmy na Twój numer telefonu{" "}
                    <span className={boldTextStyle}>{phoneNumberFormat(userInfo.phone)}</span>.{" "}
                    <span className={changeNumberButtonStyle} onClick={handlers.changeNumberButton}>
                        Zmień numer
                    </span>
                </Text>
                <FormProviderRHF {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <div className={formStyle}>
                            {digitFields.map((fieldName, index) => (
                                <InputField
                                    key={fieldName}
                                    ref={(el) => {
                                        inputRefs.current[index] = el;
                                    }}
                                    name={fieldName}
                                    maxLength={1}
                                    className={inputStyle}
                                    inputMode="numeric"
                                    autoComplete={index === 0 ? "one-time-code" : "off"}
                                    onChange={(e) => handlers.inputFocusChange(fieldName, e.target.value)}
                                    onKeyDown={(e) => handlers.inputKeyDown(fieldName, e)}
                                    onPaste={handlers.inputPaste}
                                />
                            ))}
                        </div>
                        {formError && (
                            <Text variant="info_txt_1" className={errorMessageStyle}>
                                {formError.message}
                            </Text>
                        )}
                        <Button variant="filled_primary" type="submit" fullWidth className={buttonStyle}>
                            POTWIERDŹ
                        </Button>
                        {formError?.status !== HTTP_STATUS_FOR_TOO_MANY_REQUESTS ? (
                            <Text variant="info_txt_1" as="div" className={timer > 0 ? timerStyle : sendCodeInfoStyle} onClick={sendNewCode}>
                                {isLoading ? <Loader size="sm" /> : timer > 0 ? `Wyślij kod ponownie za: ${formattedTime}` : "Wyślij kod ponownie"}
                            </Text>
                        ) : (
                            <Text variant="info_txt_1" className={timerStyle}>
                                Nie otrzymałeś kodu? <br />
                                Skontaktuj się z naszym biurem obsługi:{" "}
                                <a className={cx(timerStyle, css({fontWeight: 700}))} href={`tel:+${hotlinePhoneFormat}`}>
                                    {phoneNumberFormat(HOTLINE_INFO_PHONE_NUMBER)}
                                </a>
                            </Text>
                        )}
                    </form>
                </FormProviderRHF>
            </Modal.Content>
        </Modal>
    );
};

//Styles
const modalStyle = css({
    sm: {
        p: 2,
        borderRadius: 2,
        width: "544px",
        height: "448px"
    }
});

const formStyle = css({
    display: "flex",
    flexDirection: "row",
    gap: 1,
    mb: 4,
    height: "72px",
    width: "72px",
    justifyContent: "center",
    margin: "0 auto",

    sm: {
        mb: 3,
        height: "112px",
        width: "112px"
    }
});

const buttonStyle = css({
    alignItems: "center",
    mb: 2
});

const boldTextStyle = css({
    fontWeight: "700"
});

const changeNumberButtonStyle = css({
    textDecoration: "underline",
    cursor: "pointer"
});

const inputStyle = css({
    textAlign: "center",
    borderRadius: "8px",
    border: "1px solid var(--colors-gray-600)",
    fontSize: "30.79px",
    sm: {
        fontSize: "35.92px"
    },
    "& div": {
        border: "none"
    },
    "& > div:first-child": {
        height: "72px",
        width: "72px",
        sm: {
            height: "112px",
            width: "112px"
        },

        "& > div:first-child": {
            height: "70px",
            width: "72px",
            sm: {
                height: "110px",
                width: "112px"
            }
        }
    },
    "& input": {
        height: "100%",
        width: "100%",
        textAlign: "center",
        fontWeight: "600"
    },
    '& [data-name="field-message"]': {
        display: "flex",
        justifyContent: "center"
    }
});

const timerStyle = css({
    color: "var(--colors-gray-800)",
    textAlign: "center"
});

const sendCodeInfoStyle = css({
    color: "var(--colors-gray-800)",
    textAlign: "center",
    cursor: "pointer",
    textDecoration: "underline"
});

const errorMessageStyle = css({
    color: "var(--colors-danger)",
    textAlign: "center",
    mb: 2
});
